import React from "react";
import { css } from "glamor";
import TipTapViewer from "../../library/TipTapViewer";
import { spacing } from "../../library/theme";

const listClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 10,
});

const rowClass = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  " [data-eliminate-btn]": {
    opacity: 0,
    transition: "opacity 0.15s",
  },
  ":hover [data-eliminate-btn]": {
    opacity: 1,
  },
});

const itemClass = css({
  flex: 1,
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  background: "#ffffff",
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  overflow: "hidden",
  padding: "16px 21px",
  gap: 16,
  transition: "border-color 0.15s, background 0.15s",
  ":hover": {
    borderColor: "#0369a1",
    background: "#f8fafc",
  },
});

const itemDisabledClass = css({
  cursor: "not-allowed",
  ":hover": {
    borderColor: "#e2e8f0",
    background: "#ffffff",
  },
});

const itemEliminatedClass = css({
  background: "#fafbfc",
  borderColor: "#f1f5f9",
  cursor: "not-allowed",
  ":hover": {
    borderColor: "#f1f5f9",
    background: "#fafbfc",
  },
});

const selectedClass = css({
  borderColor: "#0369a1",
  background: "#f8fafc",
  ":hover": {
    borderColor: "#0369a1",
    background: "#f8fafc",
  },
});

const letterClass = css({
  width: 30,
  minWidth: 30,
  height: 30,
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f1f5f9",
  color: "#475569",
  fontWeight: 700,
  fontSize: 13,
  flexShrink: 0,
  transition: "background 0.15s, color 0.15s",
});

const selectedLetterClass = css({
  background: "#0369a1",
  color: "#ffffff",
});

const textClass = css({
  flex: 1,
  padding: 0,
  fontSize: 14,
  lineHeight: "21px",
  color: "#1e293b",
});

const eliminatedTextClass = css({
  color: "#cbd5e1",
});

const eliminatedLetterClass = css({
  color: "#cbd5e1",
});

const bodyClass = css({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
});

const justificationClass = css({
  paddingTop: spacing.s,
  marginTop: spacing.s,
  borderTop: "1px solid #e2e8f0",
  color: "#475569",
  fontSize: 11,
  lineHeight: "18px",
});

const eliminateBtnClass = css({
  width: 28,
  minWidth: 28,
  height: 28,
  borderRadius: 8,
  border: "0.66px solid #e2e8f0",
  background: "#f1f5f9",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  color: "#94a3b8",
  fontSize: 14,
  flexShrink: 0,
  padding: "0 7px",
  transition: "border-color 0.15s, color 0.15s",
  position: "relative",
  ":hover": {
    borderColor: "#94a3b8",
    color: "#64748b",
  },
  ":hover > span": {
    display: "block",
  },
});

const eliminatedBtnActiveClass = css({
  opacity: "1 !important",
  borderColor: "#e2e8f0",
  color: "#94a3b8",
  background: "#f1f5f9",
});

const tooltipClass = css({
  display: "none",
  position: "absolute",
  bottom: "calc(100% + 6px)",
  right: 0,
  background: "#0f172a",
  color: "#ffffff",
  fontSize: 11,
  fontWeight: 600,
  padding: "4px 8px",
  borderRadius: 6,
  whiteSpace: "nowrap",
  pointerEvents: "none",
  zIndex: 10,
});

const indexToLetter = (index) => String.fromCharCode(65 + index);

export default function AlternativesList({
  alternatives = [],
  selectedAlternativeId,
  disabled,
  onSelect,
  onEliminate,
  eliminatedIds,
  showJustifications = false,
}) {
  const eliminatedSet = eliminatedIds instanceof Set ? eliminatedIds : new Set(eliminatedIds || []);
  const canEliminate = !!onEliminate && !disabled;

  return (
    <div className={listClass} data-testid="alternatives-list">
      {alternatives.map((alternative, index) => {
        const isSelected =
          selectedAlternativeId != null &&
          Number(selectedAlternativeId) === Number(alternative?.id);
        const isEliminated = eliminatedSet.has(alternative?.id);
        const isClickable = !disabled && !isEliminated;

        const itemModifiers = css(
          itemClass,
          disabled && !isEliminated && itemDisabledClass,
          isEliminated && itemEliminatedClass,
          isSelected && selectedClass
        );

        return (
          <div
            key={alternative?.id || index}
            className={rowClass}
            data-testid="alternative-row"
            data-alternative-id={alternative?.id}
            data-alternative-letter={indexToLetter(index)}
            data-alternative-selected={isSelected ? "true" : "false"}
            data-alternative-eliminated={isEliminated ? "true" : "false"}
          >
            <div
              className={itemModifiers}
              data-testid="alternative-item"
              onClick={isClickable ? () => onSelect && onSelect(alternative) : undefined}
            >
              <div className={css(letterClass, isSelected && selectedLetterClass, isEliminated && eliminatedLetterClass)}>
                {indexToLetter(index)}
              </div>
              <div className={bodyClass}>
                <TipTapViewer
                  content={alternative?.content}
                  className={css(textClass, isEliminated && eliminatedTextClass)}
                />
                {showJustifications && alternative?.justification && (
                  <TipTapViewer
                    content={alternative.justification}
                    className={justificationClass}
                  />
                )}
              </div>
            </div>

            {canEliminate && (
              <div
                data-eliminate-btn
                data-testid="alternative-eliminate-btn"
                className={css(eliminateBtnClass, isEliminated && eliminatedBtnActiveClass)}
                onClick={() => onEliminate(alternative?.id)}
                title="Eliminar alternativa"
              >
                ×
                <span className={tooltipClass}>Eliminar alternativa</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
